import { Spin, SpinImageSet, SpinnerData } from '|/services/vex-spinner';
import { parseSourceDimensions } from './crop';
import { SpinFrame } from './types';

/** RIDE specified frame 9 of the 64-frame spin directly; it sits between documented angles. */
export const SPIN_FRAME_INDEX = 9;

const PREFERRED_CAMERA_HEIGHT = 5;
const PREFERRED_STYLE = 'edc';

const frameFromImageSet = (
  spin: Spin,
  images: SpinImageSet | null | undefined,
  frameIndex: number
): SpinFrame | null => {
  const url = images?.urls?.[frameIndex];

  if (!url) return null;

  return {
    url,
    source: parseSourceDimensions(images?.dimensions),
    boundingBox: spin.boundingBox ?? null
  };
};

const framesForSpin = (spin: Spin, frameIndex: number): (SpinFrame | null)[] => [
  frameFromImageSet(spin, spin.cleanedFloor?.images, frameIndex),
  frameFromImageSet(spin, spin.blur?.images, frameIndex),
  frameFromImageSet(spin, spin.images, frameIndex),
  spin.cleanedFloor?.heroImageUrl ? { url: spin.cleanedFloor.heroImageUrl } : null,
  spin.heroImageUrl ? { url: spin.heroImageUrl } : null
];

/**
 * RIDE's selector: `cameraHeight === 5 && style === 'edc'`, then `cleanedFloor.images.urls[9]`.
 * Every level is guarded — the 7ft `edo` spin has `cleanedFloor: null` and non-dome inventory
 * populates `blur` instead.
 */
export const selectSpinFrames = (
  spinnerData?: SpinnerData | null,
  frameIndex: number = SPIN_FRAME_INDEX
): SpinFrame[] => {
  const spins = (spinnerData?.spins ?? []).filter((spin): spin is Spin => !!spin);
  const isPreferred = (spin: Spin) =>
    spin.cameraHeight === PREFERRED_CAMERA_HEIGHT && spin.style === PREFERRED_STYLE;
  const ordered = [...spins.filter(isPreferred), ...spins.filter(spin => !isPreferred(spin))];

  const seen = new Set<string>();

  return ordered.flatMap(spin =>
    framesForSpin(spin, frameIndex).filter((frame): frame is SpinFrame => {
      if (!frame || seen.has(frame.url)) return false;

      seen.add(frame.url);
      return true;
    })
  );
};
